// Invoice generation service — prepare + persist for generate, correction, register.
// No Fakturownia-specific logic. No `any` types. Uses DAOs exclusively.
// Pattern: prepare() builds complete DB rows → POST to Fakturownia → persist() saves with external fields.

import { InvoiceDao } from '../InvoiceDao.ts';
import { CompanyDao } from '../../company/CompanyDao.ts';
import { TransactionDao } from '../../transaction/TransactionDao.ts';
import { ProductDao } from '../../product/ProductDao.ts';
import { applyCorrections } from '../effectiveLineItems.ts';
import { sumAmounts, moneyDiff } from '../../../calc.ts';
import { SIDE_COLUMNS } from '../../../sideFields.ts';
import type { SupabaseClient, InvalidateFn } from '../../base.ts';
import type { Transaction, TransactionRow } from '@shared/types/transaction.ts';
import type { Product } from '@shared/types/product.ts';
import type { GTUCode } from '@shared/types.ts';
import type { InvoiceRow, InvoiceLineInsert, InvoiceLinkInsert } from '@shared/types/invoice.ts';
import type { EffectiveLineItem } from '../effectiveLineItems.ts';
import type { Company } from '@shared/types/company.ts';
import type { PricingSide, PriceType, VatRateCode } from '../../../types.ts';
import type {
  PreparedInvoice,
  PreparedCorrection,
  RegisterInvoiceInput,
} from '@shared/types/invoice.ts';
import type { BankAccount } from '@shared/types/company.ts';
import { validateTransactions } from '../../../invoiceHelpers.ts';
import {
  getSidePricing,
  getSideCompany,
  getSideCompanyId,
  getSideDate,
  getSideLocationCompany,
  determineDates,
  determineIncome,
  buildPreparedLineItems,
  buildCorrectionLineItems,
  buildSummary,
  buildInvoiceLanguage,
} from './helpers.ts';
import {
  computeCorrectionDiff as pureCorrectionDiff,
  type CorrectionEditRow,
  type CorrectionDiff,
} from '../reconcileMath.ts';
import type { CorrectionLineItem } from '@shared/types/invoice.ts';

/** Fields returned by Fakturownia after POST. */
export interface FakturowniaPostFields {
  external_system_id: string;
  invoice_number: string;
  external_checksum: string;
  link: string;
}

/**
 * Per-row payload for `commitCorrectionEdits`. Only fields present
 * (`!== undefined`) are persisted — `undefined` means "no change". A
 * `removed: true` entry skips all writes and just contributes the
 * `transaction_id` to the returned `removedTransactionIds`.
 */
export interface CorrectionEdit {
  transaction_id: number;
  removed:        boolean;
  weight?:        number;
  unit_price?:    number;
  vat_rate_code?: VatRateCode;
  price_type?:    PriceType;
  /** Required to apply a GTU edit — `null` means "clear". */
  product_id?:    number | null;
  gtu_code?:      GTUCode | null;
}

// Side-column mapping comes from `shared/sideFields.ts` (`SIDE_COLUMNS`)
// — same map the FE `getSideColumnNames` helper exposes, so there's only
// one source of truth for which DB columns hold load/unload pricing.

export class InvoiceGenerationService {
  readonly invoiceDao: InvoiceDao;
  private readonly companyDao: CompanyDao;
  private readonly txDao: TransactionDao;
  private readonly productDao: ProductDao;

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    this.invoiceDao = new InvoiceDao(sb, invalidate);
    this.companyDao = new CompanyDao(sb, invalidate);
    this.txDao = new TransactionDao(sb, invalidate);
    this.productDao = new ProductDao(sb, invalidate);
  }

  // ── Generate (issued invoice) ───────────────────────────────────────

  async prepareInvoice(transactionIds: number[], side: PricingSide): Promise<PreparedInvoice> {
    const transactions = await this.txDao.getByIds(transactionIds);
    if (transactions.length === 0) throw new Error('No valid transactions found');

    const warnings: string[] = [];
    if (transactions.length !== transactionIds.length) {
      warnings.push(`Found ${transactions.length} transactions but ${transactionIds.length} were requested`);
    }

    // validateTransactions' callback is declared with an untyped `tx` (shared/invoiceHelpers.ts);
    // annotating here restores the real row type without changing the call.
    const validation = validateTransactions(transactions, side, (tx: Transaction, s) => {
      const pricing = getSidePricing(tx, s);
      return { companyId: getSideCompanyId(tx, s), currency: pricing.currency, price: pricing.unit_price, date: getSideDate(tx, s) };
    });
    if (!validation.isValid) warnings.push(...validation.errors);

    const client = getSideCompany(transactions[0], side);
    const summary = buildSummary(transactions, side);
    const ownCompany = await this.fetchOwnCompany();
    const bankAccount = await this.fetchBankAccount(ownCompany?.id ?? null, summary.currency);
    const countryLang = client?.country_code ? await this.fetchCountryLanguage(client.country_code) : 'en';
    const language = buildInvoiceLanguage(countryLang);
    const lineItems = buildPreparedLineItems(transactions, side, language);
    const { issueDate, sellDate, paymentDate, issuePlace } = determineDates(
      transactions, side, ownCompany?.city, client?.payment_terms, undefined,
    );
    const income = determineIncome(transactions, side);
    const locationCompany = getSideLocationCompany(transactions[0], side);
    const receiverDiffFromBuyer = locationCompany && client && locationCompany.id !== client.id;

    // Build DB-ready rows
    const invoiceRow: Partial<InvoiceRow> = {
      invoice_type: 'original', direction: 'issued', source: 'fakturownia',
      invoice_date: issueDate, issue_date: issueDate, sell_date: sellDate,
      payment_date: paymentDate, issue_place: issuePlace,
      seller_company_id: ownCompany?.id, buyer_company_id: client?.id,
      recipient_company_id: receiverDiffFromBuyer ? locationCompany.id : null,
      net_amount: summary.total_net, vat_amount: summary.total_vat, gross_amount: summary.total_gross,
      currency: summary.currency,
      seller_bank_account: bankAccount?.account_number ?? null,
      seller_bank_name: bankAccount?.bank_name ?? null,
      split_payment: summary.currency === 'PLN',
      sync_status: 'synced', review_status: 'approved', payment_status: 'pending',
    };

    const lineItemRows: InvoiceLineInsert[] = lineItems.map(li => ({
      invoice_id: 0, ordinal: li.ordinal, name: li.name, description: li.description,
      gtu_code: li.gtu_code, product_id: null, ...li.pricing,
    }));

    const linkRows: InvoiceLinkInsert[] = lineItems.map(li => ({
      invoice_id: 0, invoice_line_item_id: null, transaction_id: li.transaction_id,
      transaction_transport_id: null, side, stale: false,
      // Operator-generated link — stamp 'manual' so the auto-reconcile sweep
      // (which deletes source='auto' rows) never erases it. The generated
      // invoice arrives 'approved' with no reconcile_report, so the first sweep
      // would otherwise treat these as auto and delete them (finding A9/1).
      source: 'manual',
    }));

    return {
      line_items: lineItems, summary, income, language, warnings,
      invoiceRow, lineItemRows, linkRows,
      seller: ownCompany, buyer: client,
      receiver: receiverDiffFromBuyer ? locationCompany : null,
      bank_account: bankAccount, buyer_payment_terms: client?.payment_terms ?? null,
    };
  }

  // ── Correction ──────────────────────────────────────────────────────

  async prepareCorrection(
    invoiceId: number,
    opts?: { removedTransactionIds?: number[] },
  ): Promise<PreparedCorrection> {
    const removedTxIds = new Set(opts?.removedTransactionIds ?? []);
    const invoice = await this.invoiceDao.getWithCompanies(invoiceId);
    if (invoice.invoice_type !== 'original') throw new Error('Can only create corrections for original invoices');
    if (!invoice.external_system_id) throw new Error('Invoice not synced to Fakturownia');

    const originalLineItems = await this.invoiceDao.getLineItems(invoiceId);
    if (originalLineItems.length === 0) throw new Error('No line items found');

    const corrections = invoice.corrections?.filter(c => !c.is_deleted) ?? [];
    let effectivePricing: EffectiveLineItem[] | null = null;
    if (corrections.length > 0) {
      const corrIds = corrections.map(c => c.id);
      const corrLineItemsMap = await this.invoiceDao.getLineItemsForInvoices(corrIds);
      const allCorrLineItems = [...corrLineItemsMap.values()].flat();
      if (allCorrLineItems.length > 0) {
        effectivePricing = applyCorrections(originalLineItems, allCorrLineItems);
      }
    }

    const links = await this.invoiceDao.getLinkRows(invoiceId);
    if (links.length === 0) throw new Error('No transaction links found');

    const txIds = links.map(l => l.transaction_id).filter((id): id is number => id != null);
    const transactions = await this.txDao.getByIds(txIds);
    const txMap = new Map(transactions.map(tx => [tx.id, tx]));

    const side: PricingSide = links[0].side ?? 'load';
    const countryLang = invoice.buyer_company?.country_code
      ? await this.fetchCountryLanguage(invoice.buyer_company.country_code) : 'pl';
    const language = buildInvoiceLanguage(countryLang);

    const allTransactions = buildCorrectionLineItems(links, originalLineItems, txMap, effectivePricing, side, language);

    const active = allTransactions.filter(ct => ct.pricing.original && !removedTxIds.has(ct.transaction_id));
    const afterTotals = sumAmounts(active.map(ct => ct.pricing));
    // `active` already filtered on a truthy pricing.original; flatMap re-states that
    // for the type checker without changing which entries are summed.
    const beforeTotals = sumAmounts(active.flatMap(ct => ct.pricing.original ? [ct.pricing.original] : []));
    const diff = {
      net: moneyDiff(afterTotals.net, beforeTotals.net),
      vat: moneyDiff(afterTotals.vat, beforeTotals.vat),
      gross: moneyDiff(afterTotals.gross, beforeTotals.gross),
    };

    const sellerBankAccount = invoice.seller_company
      ? await this.fetchBankAccount(invoice.seller_company.id, invoice.currency) : null;

    const invoiceRow: Partial<InvoiceRow> = {
      invoice_type: 'correction', direction: 'issued', source: 'fakturownia',
      correction_of_invoice_id: invoiceId,
      seller_company_id: invoice.seller_company?.id, buyer_company_id: invoice.buyer_company?.id,
      net_amount: diff.net, vat_amount: diff.vat, gross_amount: diff.gross,
      currency: invoice.currency,
      seller_bank_account: sellerBankAccount?.account_number ?? null,
      seller_bank_name: sellerBankAccount?.bank_name ?? null,
      split_payment: invoice.split_payment,
      sync_status: 'synced', review_status: 'approved', payment_status: 'pending',
    };

    return {
      original_invoice_id: invoice.id,
      original_external_system_id: invoice.external_system_id,
      side,
      all_transactions: allTransactions,
      summary: { diff_net: diff.net, diff_vat: diff.vat, diff_gross: diff.gross, currency: invoice.currency },
      warnings: [],
      invoiceRow, lineItemRows: [], linkRows: [],
      original_invoice_number: invoice.invoice_number,
      seller: invoice.seller_company,
      buyer: invoice.buyer_company,
      bank_account: sellerBankAccount,
    };
  }

  // ── Correction · preview diff (pure) ─────────────────────────────────
  //
  // Sync wrapper around reconcileMath.computeCorrectionDiff. The form calls
  // this on every keystroke to render the Δ Netto / Δ VAT / Δ Brutto
  // footer; the service surface keeps the math in one place across FE +
  // edge-fn + Cloud Run.

  previewCorrectionDiff(
    editRows: Map<number, CorrectionEditRow>,
    all: CorrectionLineItem[],
    currency: string,
  ): CorrectionDiff {
    return pureCorrectionDiff(editRows, all, currency);
  }

  // ── Correction · commit per-row edits ───────────────────────────────
  //
  // Persist the operator's per-row edits before the Fakturownia edge fn
  // re-issues the invoice. Each `CorrectionEdit` mirrors what the form
  // can change in one row; `removed: true` skips writes and just
  // contributes the id to the return value.
  //
  // Routes every write through a typed DAO:
  //   - transaction price / weight / VAT / price-type → TransactionDao
  //   - product gtu_code                              → ProductDao
  // The form never holds a Supabase client.

  async commitCorrectionEdits(
    side: PricingSide,
    edits: CorrectionEdit[],
  ): Promise<{ removedTransactionIds: number[] }> {
    const cols = SIDE_COLUMNS[side];
    const removedTransactionIds: number[] = [];

    for (const edit of edits) {
      if (edit.removed) {
        removedTransactionIds.push(edit.transaction_id);
        continue;
      }
      const txUpdate: Partial<TransactionRow> = {};
      if (edit.weight        !== undefined) (txUpdate as Record<string, unknown>)[cols.weight]      = edit.weight;
      if (edit.unit_price    !== undefined) (txUpdate as Record<string, unknown>)[cols.price]       = edit.unit_price;
      if (edit.vat_rate_code !== undefined) (txUpdate as Record<string, unknown>)[cols.vatRateCode] = edit.vat_rate_code;
      if (edit.price_type    !== undefined) (txUpdate as Record<string, unknown>)[cols.priceType]   = edit.price_type;
      if (Object.keys(txUpdate).length > 0) {
        await this.txDao.update(edit.transaction_id, txUpdate);
      }
      if (edit.gtu_code !== undefined && edit.product_id != null) {
        await this.productDao.update(edit.product_id, { gtu_code: edit.gtu_code } as Partial<Product>);
      }
    }

    return { removedTransactionIds };
  }

  // ── Register received ───────────────────────────────────────────────

  async buildReceivedInvoice(invoiceData: RegisterInvoiceInput): Promise<{
    invoiceRow: Partial<InvoiceRow>;
    lineItemRows: InvoiceLineInsert[];
    linkRows: InvoiceLinkInsert[];
  }> {
    const ownCompany = await this.fetchOwnCompany();
    const { line_items, transaction_ids, side, ...invoiceFields } = invoiceData;
    const totals = sumAmounts(line_items);

    const invoiceRow: Partial<InvoiceRow> = {
      ...invoiceFields,
      invoice_type: 'original', direction: 'received', source: 'manual',
      buyer_company_id: ownCompany?.id,
      net_amount: totals.net, vat_amount: totals.vat, gross_amount: totals.gross,
      sync_status: 'synced',
      review_status: transaction_ids?.length ? 'approved' : 'pending',
      payment_status: 'pending',
    };

    const lineItemRows: InvoiceLineInsert[] = line_items.map((li, idx) => ({
      ...li, invoice_id: 0, ordinal: idx + 1,
    } as InvoiceLineInsert));

    const linkRows: InvoiceLinkInsert[] = (transaction_ids ?? []).map(txId => ({
      invoice_id: 0, transaction_id: txId, transaction_transport_id: null,
      side: side ?? null, invoice_line_item_id: null, stale: false,
      // Operator-registered link — stamp 'manual' so the auto-reconcile sweep
      // never erases it (received invoice is set 'approved' with no report;
      // an 'auto' link would be deleted on the first sweep — finding A9/1).
      source: 'manual',
    }));

    return { invoiceRow, lineItemRows, linkRows };
  }

  // ── Unified persist ─────────────────────────────────────────────────

  /** Persist prepared invoice rows + Fakturownia response fields to DB. */
  async persist(
    built: { invoiceRow: Partial<InvoiceRow>; lineItemRows: InvoiceLineInsert[]; linkRows: InvoiceLinkInsert[] },
    fakturownia: FakturowniaPostFields,
  ): Promise<{ invoiceId: number; invoiceNumber: string }> {
    const invoice = await this.invoiceDao.create({
      ...built.invoiceRow,
      invoice_number: fakturownia.invoice_number || built.invoiceRow.invoice_number,
      external_system_id: fakturownia.external_system_id,
      external_checksum: fakturownia.external_checksum,
      link: fakturownia.link,
    }, { invalidate: false });

    let createdLineItems: { id: number }[] = [];
    if (built.lineItemRows.length > 0) {
      const withInvoiceId = built.lineItemRows.map(li => ({ ...li, invoice_id: invoice.id }));
      createdLineItems = await this.invoiceDao.createLineItems(withInvoiceId, { invalidate: false });
    }

    if (built.linkRows.length > 0) {
      const withIds = built.linkRows.map((link, idx) => ({
        ...link, invoice_id: invoice.id,
        invoice_line_item_id: createdLineItems[idx]?.id ?? link.invoice_line_item_id ?? null,
      }));
      await this.invoiceDao.createLinks(withIds, { invalidate: false });
    }

    if (built.invoiceRow.correction_of_invoice_id) {
      await this.invoiceDao.clearStaleLinks(built.invoiceRow.correction_of_invoice_id, { invalidate: false });
    }

    return { invoiceId: invoice.id, invoiceNumber: fakturownia.invoice_number || built.invoiceRow.invoice_number || '' };
  }

  // ── Private helpers ─────────────────────────────────────────────────

  private async fetchOwnCompany(): Promise<Company | null> {
    return this.companyDao.getOwnCompany() as Promise<Company | null>;
  }

  private async fetchBankAccount(companyId: number | null, currency: string): Promise<BankAccount | null> {
    if (!companyId) return null;
    return this.companyDao.getDefaultBankAccount(companyId, currency);
  }

  private async fetchCountryLanguage(countryCode: string): Promise<string> {
    return this.companyDao.getCountryLanguage(countryCode);
  }
}
